/**
 * 打印模板模块 - 模板字段定义
 * 
 * @module types/print-template/fields
 * @description 统一定义各模板类型支持的变量字段，Web端和App端共用
 * @author YSFL Team
 * @date 2025-12-16
 */

import { PrintTemplateType } from './enums'

/**
 * 模板字段定义接口
 */
export interface TemplateField {
  /** 字段键名（用于模板变量替换，如 {{materialCode}}） */
  key: string
  /** 字段标签（中文名称，用于UI展示） */
  label: string
  /** 示例值（用于设计器预览） */
  sample: string
}

/**
 * 获取当前打印时间（用于示例值）
 */
function getPrintTime(): string {
  return new Date().toLocaleString('zh-CN')
}

// ============================================================
// 核心模板字段定义（物料标签、工单标签、设备标签）
// ============================================================

/**
 * 物料标签字段 (templateType = 3)
 * 
 * 使用场景：采购收货、库存批次、物料打印
 * App端模板：material-label.json, material-label-large.json, material-label-small.json
 */
export const MATERIAL_LABEL_FIELDS: TemplateField[] = [
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialId', label: '物料ID', sample: '101' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'specification', label: '规格型号', sample: '100x50x2mm' },
  { key: 'unit', label: '单位', sample: '张' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20251213' },
  { key: 'heatNo', label: '炉号', sample: 'HT20251213-001' },
  { key: 'quantity', label: '数量', sample: '100' },
  { key: 'supplierName', label: '供应商', sample: '供应商A' },
  { key: 'supplierBatchNo', label: '来料批次号', sample: 'SUP-BAT-001' },
  { key: 'drawingNo', label: '图号', sample: 'DWG-001' },
  { key: 'productionDate', label: '生产日期', sample: '2025-12-13' },
  { key: 'expiryDate', label: '有效期', sample: '2026-12-13' },
  { key: 'inboundDate', label: '入库日期', sample: '2025-12-13' },
  { key: 'warehouseCode', label: '仓库编码', sample: 'WH-001' },
  { key: 'warehouseName', label: '仓库名称', sample: '原材料仓' },
  { key: 'locationCode', label: '库位编码', sample: 'A-01-01' },
  { key: 'qualityStatus', label: '质量状态', sample: 'PENDING' },
  { key: 'qualityStatusText', label: '质量状态文本', sample: '待检' },
  { key: 'sourceNo', label: '来源单号', sample: 'PO20251213001' },
  { key: 'qrContent', label: '二维码内容', sample: '{"v":1,"type":"MATERIAL","materialCode":"MAT-001","batchNo":"BAT20251213"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 工单标签字段 (templateType = 5)
 * 
 * 使用场景：生产工单打印
 * App端模板：work-order-label.json, work-order-label-large.json
 */
export const WORK_ORDER_LABEL_FIELDS: TemplateField[] = [
  { key: 'workOrderNo', label: '工单编号', sample: 'WO20251213001' },
  { key: 'materialCode', label: '物料编码', sample: 'PROD-001' },
  { key: 'materialName', label: '物料名称', sample: '成品A' },
  { key: 'workOrderQuantity', label: '工单数量', sample: '100' },
  { key: 'lineName', label: '产线名称', sample: '一号产线' },
  { key: 'planStartDate', label: '工单计划开始', sample: '2025-12-13' },
  { key: 'planEndDate', label: '工单计划结束', sample: '2025-12-14' },
  { key: 'remark', label: '备注', sample: '加急，注意外观' },
  { key: 'priority', label: '优先级', sample: '普通' },
  { key: 'customerName', label: '客户名称', sample: '客户A' },
  {
    key: 'processChecklist',
    label: '工序勾选清单',
    sample: '下料 □\n清洗 □\n车加工 □\n检验 □\n入库 □',
  },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"WORK_ORDER","workOrderNo":"WO20251213001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 设备标签字段 (templateType = 6)
 * 
 * 使用场景：设备资产管理
 * App端模板：equipment-label.json
 */
export const EQUIPMENT_LABEL_FIELDS: TemplateField[] = [
  { key: 'equipmentCode', label: '设备编码', sample: 'EQ-CNC-001' },
  { key: 'equipmentName', label: '设备名称', sample: '数控加工中心' },
  { key: 'model', label: '型号', sample: 'VMC-850' },
  { key: 'serialNumber', label: '序列号', sample: 'SN20251208001' },
  { key: 'equipmentType', label: '设备类型', sample: 'CNC加工设备' },
  { key: 'workstationCode', label: '工位编码', sample: 'WS-A01' },
  { key: 'manufacturer', label: '制造商', sample: '制造商A' },
  { key: 'purchaseDate', label: '购置日期', sample: '2024-01-15' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"EQUIPMENT","equipmentCode":"EQ-CNC-001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 货位标签字段 (templateType = 9)
 */
export const LOCATION_LABEL_FIELDS: TemplateField[] = [
  { key: 'locationCode', label: '货位编码', sample: 'A-01-01' },
  { key: 'locationId', label: '货位ID', sample: '201' },
  { key: 'locationName', label: '货位名称', sample: 'A区1号架1层' },
  { key: 'warehouseCode', label: '仓库编码', sample: 'WH-001' },
  { key: 'warehouseName', label: '仓库名称', sample: '原材料仓' },
  { key: 'zone', label: '分区', sample: 'A' },
  { key: 'shelf', label: '货架', sample: '01' },
  { key: 'layer', label: '层', sample: '01' },
  { key: 'bin', label: '位', sample: '01' },
  { key: 'qrContent', label: '二维码内容', sample: '{"v":1,"type":"LOCATION","locationId":201,"locationCode":"A-01-01"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 物料货位标签字段 (templateType = 10)
 */
export const MATERIAL_LOCATION_LABEL_FIELDS: TemplateField[] = [
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialId', label: '物料ID', sample: '101' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'specification', label: '规格型号', sample: '100x50x2mm' },
  { key: 'warehouseCode', label: '仓库编码', sample: 'WH-001' },
  { key: 'warehouseName', label: '仓库名称', sample: '原材料仓' },
  { key: 'locationCode', label: '货位编码', sample: 'A-01-01' },
  { key: 'locationName', label: '货位名称', sample: 'A区1号架1层' },
  { key: 'unit', label: '单位', sample: '张' },
  { key: 'qrContent', label: '二维码内容', sample: '{"v":1,"type":"MATERIAL_LOCATION","materialId":101,"materialCode":"MAT-001","locationId":201,"locationCode":"A-01-01"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

// ============================================================
// 非核心模板字段定义（暂未使用，保留扩展）
// ============================================================

/**
 * 入库单字段 (templateType = 1)
 * 
 * TODO: 当前 App 端使用字段与 Web 端定义存在差异：
 * - App端使用: transactionNo, targetWarehouseCode, targetLocationCode, sourceDocNo, transactionTime
 * - 待统一后启用
 * 
 * @deprecated 暂未使用，等待业务需求确认后统一
 */
export const WAREHOUSE_IN_FIELDS: TemplateField[] = [
  { key: 'transactionNo', label: '入库单号', sample: 'RK20251213001' },
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20251213' },
  { key: 'quantity', label: '数量', sample: '100' },
  { key: 'unit', label: '单位', sample: '张' },
  { key: 'targetWarehouseCode', label: '目标仓库', sample: 'WH-001' },
  { key: 'targetLocationCode', label: '目标库位', sample: 'A-01-01' },
  { key: 'sourceDocNo', label: '来源单据', sample: 'PO20251213001' },
  { key: 'operatorName', label: '操作人', sample: '张三' },
  { key: 'transactionTime', label: '入库时间', sample: '2025-12-13 10:30' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"RECEIPT","transactionNo":"RK20251213001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 出库单字段 (templateType = 2)
 * 
 * TODO: 当前 App 端使用字段与 Web 端定义存在差异：
 * - App端使用: transactionNo, sourceWarehouseCode, sourceLocationCode, sourceDocNo, transactionTime
 * - 待统一后启用
 * 
 * @deprecated 暂未使用，等待业务需求确认后统一
 */
export const WAREHOUSE_OUT_FIELDS: TemplateField[] = [
  { key: 'transactionNo', label: '出库单号', sample: 'CK20251213001' },
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20251213' },
  { key: 'quantity', label: '数量', sample: '50' },
  { key: 'unit', label: '单位', sample: '张' },
  { key: 'sourceWarehouseCode', label: '来源仓库', sample: 'WH-001' },
  { key: 'sourceLocationCode', label: '来源库位', sample: 'A-01-01' },
  { key: 'sourceDocNo', label: '领用单据', sample: 'MR20251213001' },
  { key: 'operatorName', label: '操作人', sample: '李四' },
  { key: 'transactionTime', label: '出库时间', sample: '2025-12-13 14:30' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"ISSUE","transactionNo":"CK20251213001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 产品标签字段 (templateType = 4)
 * 
 * TODO: 当前 App 端使用字段与 Web 端定义存在较大差异：
 * - App端使用: workOrderNo, productName, quantity, operatorName, reportTime, batchNo, currentQuantity, containerCode, printedByName, printedAt
 * - Web端定义: productNo, productName, specification, batchNo, serialNo, quantity, productionDate, qualityStatus
 * - 待统一后启用
 * 
 * @deprecated 暂未使用，等待业务需求确认后统一
 */
export const PRODUCT_LABEL_FIELDS: TemplateField[] = [
  { key: 'workOrderNo', label: '工单编号', sample: 'WO20251213001' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20251213' },
  { key: 'materialName', label: '物料名称', sample: '成品A' },
  { key: 'quantity', label: '数量', sample: '100' },
  { key: 'currentQuantity', label: '当前数量', sample: '50' },
  { key: 'containerCode', label: '容器编码', sample: 'BOX-001' },
  { key: 'operatorName', label: '操作人', sample: '王五' },
  { key: 'printedByName', label: '打印人', sample: '张三' },
  { key: 'heatNo', label: '炉号', sample: 'HT20251213-001' },
  { key: 'printedAt', label: '打印时间', sample: '2025-12-13 15:00' },
  { key: 'printTime', label: '打印时间（兼容）', sample: '2025-12-13 15:00' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"PRODUCT","batchNo":"BAT20251213"}' },
]

/**
 * 工装标签字段 (templateType = 7)
 * 
 * TODO: App 端暂无此模板，待开发
 * 
 * @deprecated 暂未使用，等待 App 端实现
 */
export const TOOLING_LABEL_FIELDS: TemplateField[] = [
  { key: 'toolingCode', label: '工装编码', sample: 'TL-001' },
  { key: 'toolingName', label: '工装名称', sample: '冲压模具A' },
  { key: 'toolingType', label: '工装类型', sample: '模具' },
  { key: 'specification', label: '规格型号', sample: 'M-100' },
  { key: 'manufacturer', label: '制造商', sample: '制造商A' },
  { key: 'maxLifetime', label: '最大寿命', sample: '10000' },
  { key: 'currentUsage', label: '当前使用量', sample: '5000' },
  { key: 'remainingLife', label: '剩余寿命', sample: '5000' },
  { key: 'status', label: '状态', sample: '在用' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"TOOLING","toolingCode":"TL-001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

/**
 * 质检单字段 (templateType = 8)
 * 
 * TODO: App 端暂无此模板，待开发
 * 
 * @deprecated 暂未使用，等待 App 端实现
 */
export const INSPECTION_LABEL_FIELDS: TemplateField[] = [
  { key: 'taskNo', label: '检验任务号', sample: 'QC20251213001' },
  { key: 'inspectionType', label: '检验类型', sample: 'IPQC' },
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20251213' },
  { key: 'lotQuantity', label: '批量', sample: '100' },
  { key: 'sampleQuantity', label: '抽样数量', sample: '5' },
  { key: 'inspector', label: '检验员', sample: '王五' },
  { key: 'inspectionDate', label: '检验日期', sample: '2025-12-13' },
  { key: 'result', label: '检验结果', sample: '合格' },
  { key: 'qualifiedQuantity', label: '合格数量', sample: '5' },
  { key: 'rejectedQuantity', label: '不合格数量', sample: '0' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"INSPECTION","taskNo":"QC20251213001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

// ============================================================
// 质量单据标签字段（NCR单/返工单/报废单）
// 与 App 端 PrintDataBuilder.buildNcrOrderLabel / buildReworkOrderLabel /
// buildScrapOrderLabel 输出字段及 core-php 模板种子占位符保持一致
// ============================================================

export const NCR_ORDER_FIELDS: TemplateField[] = [
  { key: 'ncrNo', label: 'NCR编号', sample: 'NCR202609150001' },
  { key: 'productName', label: '产品名称', sample: '铝合金板材' },
  { key: 'productCode', label: '产品编码', sample: 'MAT-001' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20260915' },
  { key: 'defectQuantity', label: '不合格数量', sample: '2' },
  { key: 'defectLevelName', label: '缺陷等级', sample: '主要' },
  { key: 'sourceTypeName', label: '来源', sample: '自检' },
  { key: 'defectDescription', label: '缺陷描述', sample: '表面划伤' },
  { key: 'responsiblePersonName', label: '责任人', sample: '李四' },
  { key: 'statusName', label: '状态', sample: '待确认' },
  { key: 'confirmedName', label: '确认接收人', sample: '李四' },
  { key: 'confirmedAt', label: '确认接收时间', sample: '2026-09-15 10:00:00' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"NCR","ncrNo":"NCR202609150001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

export const REWORK_ORDER_FIELDS: TemplateField[] = [
  { key: 'reworkNo', label: '返工单号', sample: 'RW202609150001' },
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'sourceBatchNo', label: '来源批次', sample: 'BAT20260915' },
  { key: 'reworkBatchNo', label: '返工批次', sample: 'RW-BAT20260915' },
  { key: 'reworkQuantity', label: '返工数量', sample: '10' },
  { key: 'reworkTypeName', label: '返工类型', sample: '返工' },
  { key: 'sourceWorkOrderNo', label: '原工单号', sample: 'MO20260915001' },
  { key: 'reworkReason', label: '返工原因', sample: '尺寸超差' },
  { key: 'statusName', label: '状态', sample: '已通过' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"REWORK","reworkNo":"RW202609150001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

export const SCRAP_ORDER_FIELDS: TemplateField[] = [
  { key: 'scrapNo', label: '报废单号', sample: 'SC202609150001' },
  { key: 'materialCode', label: '物料编码', sample: 'MAT-001' },
  { key: 'materialName', label: '物料名称', sample: '铝合金板材' },
  { key: 'batchNo', label: '批次号', sample: 'BAT20260915' },
  { key: 'scrapQuantity', label: '报废数量', sample: '5' },
  { key: 'workOrderNo', label: '工单号', sample: 'MO20260915001' },
  { key: 'operationCode', label: '工序编码', sample: 'OP10' },
  { key: 'scrapTypeName', label: '报废类型', sample: '生产报废' },
  { key: 'disposalMethodName', label: '处置方式', sample: '报废处理' },
  { key: 'scrapReason', label: '报废原因', sample: '加工损坏' },
  { key: 'statusName', label: '状态', sample: '已通过' },
  { key: 'qrContent', label: '二维码内容', sample: '{"type":"SCRAP","scrapNo":"SC202609150001"}' },
  { key: 'printTime', label: '打印时间', sample: getPrintTime() },
]

// ============================================================
// 统一字段映射（按模板类型）
// ============================================================

/**
 * 各模板类型的字段定义映射
 * 
 * 使用方式：
 * ```typescript
 * import { TEMPLATE_FIELDS_MAP, PrintTemplateType } from '@bayes-mes/shared'
 * const fields = TEMPLATE_FIELDS_MAP[PrintTemplateType.MATERIAL_LABEL]
 * ```
 */
export const TEMPLATE_FIELDS_MAP: Record<PrintTemplateType, TemplateField[]> = {
  // 核心模板（已启用）
  [PrintTemplateType.MATERIAL_LABEL]: MATERIAL_LABEL_FIELDS,
  [PrintTemplateType.WORK_ORDER_LABEL]: WORK_ORDER_LABEL_FIELDS,
  [PrintTemplateType.EQUIPMENT_LABEL]: EQUIPMENT_LABEL_FIELDS,

  // 非核心模板（暂未使用）
  [PrintTemplateType.WAREHOUSE_IN]: WAREHOUSE_IN_FIELDS,
  [PrintTemplateType.WAREHOUSE_OUT]: WAREHOUSE_OUT_FIELDS,
  [PrintTemplateType.PRODUCT_LABEL]: PRODUCT_LABEL_FIELDS,
  [PrintTemplateType.TOOLING_LABEL]: TOOLING_LABEL_FIELDS,
  [PrintTemplateType.INSPECTION_LABEL]: INSPECTION_LABEL_FIELDS,
  [PrintTemplateType.LOCATION_LABEL]: LOCATION_LABEL_FIELDS,
  [PrintTemplateType.MATERIAL_LOCATION_LABEL]: MATERIAL_LOCATION_LABEL_FIELDS,

  // 质量单据标签
  [PrintTemplateType.NCR_ORDER]: NCR_ORDER_FIELDS,
  [PrintTemplateType.REWORK_ORDER]: REWORK_ORDER_FIELDS,
  [PrintTemplateType.SCRAP_ORDER]: SCRAP_ORDER_FIELDS,

  // 其他类型（无预设字段）
  [PrintTemplateType.OTHER]: [],
}

/**
 * 根据模板类型获取字段定义
 */
export function getTemplateFields(templateType: PrintTemplateType): TemplateField[] {
  return TEMPLATE_FIELDS_MAP[templateType] || []
}

/**
 * 根据模板类型生成测试数据
 */
export function generateTestData(templateType: PrintTemplateType): Record<string, string> {
  const fields = getTemplateFields(templateType)
  const testData: Record<string, string> = {}
  fields.forEach(field => {
    testData[field.key] = field.sample
  })
  return testData
}
